import React, { useEffect, useRef, useState } from "react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../common/SafeIcon";
import { useClient } from "../../context/ClientContext";
import {
  getClientAccountantOverview,
} from "../../services/client/clientAccountantService";
import ClientAccountingFirmCard from "../../components/clients/accountant/ClientAccountingFirmCard";
import ClientAccountingTeamList from "../../components/clients/accountant/ClientAccountingTeamList";
import ClientAccountantContactCard from "../../components/clients/accountant/ClientAccountantContactCard";

const { FiRefreshCw, FiAlertCircle } = FiIcons;

export default function ClientMyAccountant() {
  const { activeWorkspace } = useClient();
  const organizationId = activeWorkspace?.id;
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setOverview(null);
    setError("");

    if (!organizationId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    getClientAccountantOverview(organizationId)
      .then((result) => {
        if (requestRef.current === requestId) setOverview(result);
      })
      .catch((loadError) => {
        if (requestRef.current === requestId) {
          setError(loadError?.message || "Unable to load accountant information.");
        }
      })
      .finally(() => {
        if (requestRef.current === requestId) setLoading(false);
      });

    return () => {
      requestRef.current += 1;
    };
  }, [organizationId]);

  const refresh = () => {
    if (!organizationId || loading) return;
    const requestId = ++requestRef.current;
    setError("");
    setLoading(true);
    getClientAccountantOverview(organizationId)
      .then((result) => {
        if (requestRef.current === requestId) setOverview(result);
      })
      .catch((loadError) => {
        if (requestRef.current === requestId) {
          setError(loadError?.message || "Unable to load accountant information.");
        }
      })
      .finally(() => {
        if (requestRef.current === requestId) setLoading(false);
      });
  };

  const firm = overview?.firm || null;
  const team = overview?.team || [];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-violet-700">
            {activeWorkspace?.name || "Your business"}
          </p>
          <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-900">
            My Accountant
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Connect with the accounting professionals managing your business.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading || !organizationId}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
        >
          <SafeIcon
            icon={FiRefreshCw}
            className={loading ? "animate-spin" : ""}
          />
          Refresh
        </button>
      </header>

      {error && (
        <div role="alert" className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accountant information could not be loaded</p>
            <p className="mt-1 leading-6">{error}</p>
          </div>
        </div>
      )}

      {loading && !overview ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm" aria-live="polite">
          <span className="mx-auto block h-8 w-8 animate-spin rounded-full border-2 border-violet-100 border-t-violet-600" />
          <p className="mt-4 text-sm text-slate-600">Loading your accounting relationship…</p>
        </div>
      ) : (
        <>
          <ClientAccountingFirmCard
            firm={firm}
            clientProfile={overview?.clientProfile}
          />

          {firm && overview?.assignedAccountant && (
            <section>
              <div className="mb-3">
                <h2 className="text-base font-bold text-slate-900">
                  Your Assigned Accountant
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  One active team assignment is associated with your business.
                </p>
              </div>
              <div className="max-w-2xl">
                <ClientAccountingTeamList
                  team={[overview.assignedAccountant]}
                  loading={false}
                  profileAccessLimited={overview.profileAccessLimited}
                  membershipAccessLimited={overview.membershipAccessLimited}
                />
              </div>
            </section>
          )}

          {firm && !overview?.assignedAccountant && (
            <ClientAccountingTeamList
              team={team}
              loading={false}
              profileAccessLimited={overview?.profileAccessLimited}
              membershipAccessLimited={overview?.membershipAccessLimited}
            />
          )}

          <ClientAccountantContactCard
            team={team}
            firm={firm}
            clientProfile={overview?.clientProfile}
          />
        </>
      )}
    </div>
  );
}